'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

/**
 * Notes Module.
 * @module notes
 */

const NOTE_A = 'A';
const NOTE_A_SHARP = 'A#';
const NOTE_A_FLAT = 'Ab';

const NOTE_B = 'B';
const NOTE_B_FLAT = 'Bb';

const NOTE_C = 'C';
const NOTE_C_SHARP = 'C#';

const NOTE_D = 'D';
const NOTE_D_SHARP = 'D#';
const NOTE_D_FLAT = 'Db';

const NOTE_E = 'E';
const NOTE_E_FLAT = 'Eb';

const NOTE_F = 'F';
const NOTE_F_SHARP = 'F#';

const NOTE_G = 'G';
const NOTE_G_SHARP = 'G#';
const NOTE_G_FLAT = 'Gb';

/**
 * Gives chromatic notes starting from C
 */
const CHROMATIC_NOTES = [
  NOTE_C,
  [NOTE_C_SHARP, NOTE_D_FLAT],
  NOTE_D,
  [NOTE_D_SHARP, NOTE_E_FLAT],
  NOTE_E,
  NOTE_F,
  [NOTE_F_SHARP, NOTE_G_FLAT],
  NOTE_G,
  [NOTE_G_SHARP, NOTE_A_FLAT],
  NOTE_A,
  [NOTE_A_SHARP, NOTE_B_FLAT],
  NOTE_B,
];

/**
 * Returns `true` if a note is a sharp note.
 * @example:
 * isSharpNote('D#')  //true
 * isSharpNote('Ab')  //false
 * @function
 * @param {string} note note in string
 * @returns {boolean}
 */
const isSharpNote = (note) => {
  const matches = note.match(/#/);
  return matches && matches.index === 1 && matches[0] === '#';
};

/**
 * Returns `true` if a note is a flat note.
 * @example:
 * isFlatNote('Bb')  //true
 * isFlatNote('C#')  //false
 * @function
 * @param {string} note note in string
 * @returns {boolean}
 */
const isFlatNote = (note) => {
  const matches = note.match(/b/);
  return matches && matches.index === 1 && matches[0] === 'b';
};

/**
 * Returns note name without octave
 * Strips chord information as well
 * @example:
 * getNoteWithoutOctave('C4')  //C
 * getNoteWithoutOctave('F#5')  //F#
 * getNoteWithoutOctave('Db3')  //Db
 * @function
 * @param {string} note note in string with octave
 * @returns {string} note without octave
 */
const getNoteWithoutOctave = (note) => {
  const matches = note.match(/[A-H]/);
  const newNote = matches && matches[0];
  if (isSharpNote(note)) {
    return `${newNote}#`;
  }
  if (isFlatNote(note)) {
    return `${newNote}b`;
  }
  return newNote;
};

/**
 * Returns boolean value if octave number is present in note
 * @example:
 * containsOctaveNumber('C4')  //true
 * containsOctaveNumber('F#5')  //true
 * containsOctaveNumber('Db3')  //true
 * containsOctaveNumber('F')  //false
 * @function
 * @param {string} note note in string
 * @returns {boolean}
 */
const containsOctaveNumber = (note) => /[0-9]/.test(note);

/**
 * Return octave number
 * @example:
 * getOctaveNumber('C4')  //4
 * getOctaveNumber('F#5')  //5
 * @function
 * @param {string} note note in string
 * @returns {number}
 */
const getNoteOctave = (note) => {
  const matches = note.match(/[0-9]/);
  return matches && matches.length && Number(matches.join(''));
};

/**
 * Increases octave of a note
 * @function
 * @param {string|object} note
 * @returns {string|object} note with octave
 */
const incOctave = (note) => {
  if (typeof note === 'object') {
    return {
      note: note.note,
      octave: note.octave + 1,
    };
  }
  const matches = note.match(/[0-9]+/);

  let octave = matches && matches.length && Number(matches.join(''));
  if (typeof octave !== 'number') {
    octave = 4;
  }
  const naturalNote = getNoteWithoutOctave(note);
  return naturalNote + (octave + 1);
};

/**
 * Returns index of a note from `CHROMATIC_NOTES`.
 * @example:
 * getNoteIndex('C')  //0
 * getNoteIndex('F#')  //6
 * getNoteIndex('Db')  //1
 * getNoteIndex('F')  //5
 * @function
 * @param {string} searchNote note in string
 * @returns {number} Number from 0-11
 */
const getNoteIndex = (searchNote) => {
  let searchIndex = CHROMATIC_NOTES.indexOf(searchNote);

  // if the note is not the one with inharmonic counter part search directly
  if (searchIndex !== -1) {
    return searchIndex;
  }

  CHROMATIC_NOTES.forEach((note, i) => {
    // if is inharmonic val is an array
    if (Array.isArray(note)) {
      if (note.indexOf(searchNote) !== -1) {
        searchIndex = i;
      }
    }
  });

  return searchIndex;
};

/**
 * Get interval between two notes
 * getInterval('D', 'A')  //7
 * getInterval('C', 'Eb')  //3
 * getInterval('C', 'E5')  //3
 * @function
 * @param {note1} note1 note in string
 * @param {note2} note2 note in string
 * @returns {number} interval number
 */
const getInterval = (note1, note2) => {
  let firstNote = note1;
  let secondNote = note2;
  if (containsOctaveNumber(note1)) {
    firstNote = getNoteWithoutOctave(note1);
  }

  if (containsOctaveNumber(note2)) {
    secondNote = getNoteWithoutOctave(note2);
  }

  const note1Index = getNoteIndex(firstNote);
  const note2Index = getNoteIndex(secondNote);
  let noteInterval = 0;
  // A to E
  // 9 to 4 -> 7
  if (note1Index <= note2Index) {
    noteInterval = note2Index - note1Index;
  } else {
    noteInterval = 12 - (note1Index - note2Index);
  }

  const firstNoteOctave = getNoteOctave(note1) || 4;
  const secondNoteOctave = getNoteOctave(note2) || 4;

  if (secondNoteOctave < firstNoteOctave) {
    return 12 - noteInterval;
  }
  if (secondNoteOctave > firstNoteOctave) {
    return 12 + noteInterval;
  }
  return noteInterval;
};

/**
 * Octave Module.
 * @module octave
 */

/**
 * Get root note index of a chord adjusting inversion
 * @function
 * @param {object} {chord,inversion} options object
 * @param {number} options.chord
 * @param {number} options.inverions
 * @returns {number}
 */
const getRootNoteIndex = ({ chord, inversion }) => {
  // const totalNotes = chord.length;
  if (inversion === 0) {
    return getNoteIndex(chord[0]);
  }
  return getNoteIndex(chord[chord.length - inversion]);
};

/**
 * Append octave to chord with inversion
 * only works for triad, sixth, seventh chords
 * @function
 * @param {object} {chord,octave,inversion,objectResult} options object
 * @param {array} options.chord
 * @param {number} options.octave
 * @param {number} options.inversion
 * @param {boolean} options.objectResult
 * @returns {array}
 */
const appendOctaveChord = ({
  chord,
  octave = 4,
  inversion = 0,
  objectResult,
}) => {
  const firstNoteIndex = getNoteIndex(chord[0]);
  const rootNoteIndex = getRootNoteIndex({ chord, inversion });
  let currentOctave = octave;
  // const rootOctave = octave;
  let prevNoteIndex = 0;
  let octaveChanged = false;
  return chord.map((note, index) => {
    const currentNoteIndex = getNoteIndex(note);
    if (index !== 0) {
      prevNoteIndex = getNoteIndex(chord[index - 1]);
    }

    // if note reaches C of another octave
    // if currentNoteIndex is less than previous note index or less than firstNoteIndex
    // if first note index less than root note index
    if (
      (index !== 0 || firstNoteIndex < rootNoteIndex) &&
      (currentNoteIndex <= firstNoteIndex || currentNoteIndex < prevNoteIndex)
    ) {
      // change octave only once in 8 notes
      if (octaveChanged === false) {
        currentOctave += 1;
        octaveChanged = true;
      }
      // reset octaveChanged every 7 note of a chord
      if (index % 3 === 0) {
        octaveChanged = false;
      }
    }
    // octave change logic
    // if note reaches C of another octave
    // if currentNoteIndex is less than previous note index or less than firstNoteIndex
    //  if ((((currentNoteIndex < firstNoteIndex || currentNoteIndex < prevNoteIndex)
    //   && (currentOctave - rootOctave < 1))
    //   || (rootNoteIndex !== 0 && octaveChangeRelativeC(currentNoteIndex))
    //   || (rootNoteIndex !== 0 && currentNoteIndex < rootNoteIndex))
    //   && (currentOctave - rootOctave < 1)) {
    //   currentOctave += 1;
    // }

    if (objectResult) {
      return { note, octave: currentOctave };
    }
    return note + currentOctave;
  });
};

/**
 * Utils Module.
 * @module utils
 */

/*
 * reverse an array
 * @function
 * @param {object} {array,from} options object
 * @param {number} options.array
 * @param {number} options.from
 * @returns {array} reversed array
 */
const arrayRev = ({ array, from, to }) => {
  if (!to) {
    return [...array].reverse().slice(from);
  }
  return [...array].reverse().slice(from, to);
};

/**
 * Scales Module.
 * @module scales
 */
// import { circleBuilder } from './circle-of-fifths';
// import { arrayRev } from './utils';

/**
 * Scale Formula Array
 * Stores the interval/gaps between notes.
 * Instead of using 0.5 for half and 1 for whole, it would be easier to read and write 1 and 2.
 * 1 => semi tone interval (half step)
 * 2 => whole tone interval (whole step)
 * 3 => 3 half tones (3 half steps) and so on
 */

/**
 * Diatonic to chromatic step map
 * Note number relative to major scale
 * Stores number from 1-8 with flat and sharp information
 * Scale number array 1, 2, 3, 4, 5, 6, 7, 8
 * C, C#, D, D#, E, F, F#, G, G#, A, A#, B, C
 * note num to index map
 * 1 ->0
 * 2 -> 2
 * 3 -> 4
 * 4 -> 5
 * 5 -> 7
 * 6 -> 9
 * 7 -> 11
 * 8 -> 0
 */
const majorScaleIntervalToChromaticIndexMap = {
  1: 0,
  2: 2,
  3: 4,
  4: 5,
  5: 7,
  6: 9,
  7: 11,
  8: 0,
  9: 2,
  10: 4,
  11: 5,
  12: 7,
  13: 9,
  14: 11,
  15: 0,
  16: 2,
  17: 4,
  18: 5,
  19: 7,
  20: 9,
  21: 11,
  22: 0,
  23: 2,
  24: 4,
};

// TODO should have map for all modes
const minorScaleIntervalToChromaticIndexMap = {
  1: 0,
  2: 2,
  3: 3,
  4: 5,
  5: 7,
  6: 8,
  7: 10,
  8: 0,
  9: 2,
  10: 3,
  11: 5,
  12: 7,
  13: 8,
  14: 10,
  15: 0,
  16: 2,
  17: 3,
  18: 5,
  19: 7,
  20: 8,
  21: 10,
  22: 0,
  23: 2,
  24: 3,
};

// export const majorIntervals = [0, 2, 4, 5, 7, 9, 11];
// export const minorIntervals = [1, 3, 6, 8, 10];

// TODO: get note based on root note. For e.g. Cm scale the third, sixth and
// seventh should be flat notes is they are enharmonic notes.

/**
 * Note helper for scale. Returns single value from array of inharmonic notes.
 * By default `includeInharmonicNote` is `false`.
 * If it is set to `true`, both notes would be returned.
 * @example
 * getNote(['C#', 'Db'], false, false); //C#
 * getNote(['C#', 'Db'], true, false); //['C#', 'Db']
 * getNote(['C#', 'Db'], false, true); //'Db'
 * @function
 * @param {Object} options - Options Object
 * @param {string} options.chromaticNote - Note in string
 * @param {boolean} options.includeInharmonicNote
 * @param {boolean} options.flatNote
 * @returns {string|array} Can be string or array
 */
const getNote = ({ chromaticNote, isFlatNote }) => {
  if (Array.isArray(chromaticNote)) {
    if (isFlatNote) {
      return chromaticNote[1];
    }
    return chromaticNote[0];
  }
  return chromaticNote;
};

const isFlatInterval = (interval) => /^[b]+/.test(interval);

const isSharpInterval = (interval) => /^[#]+/.test(interval);

/**
 * Get scale interval number
 * @example
 * getNumberFromIntervalString('b3'); //3
 * getNumberFromIntervalString('11'); //11
 * @function
 * @param {string} intervalString - interval in string/number
 * @returns {number}
 */
const getNumberFromIntervalString = (intervalString) => {
  const matches = String(intervalString).match(/[^.b#]/g);
  // if number is greater than 9, it returns two digit num in array like ['1', '1'];
  // concat and cast to Number before returning
  return matches && matches.length && Number(matches.join(''));
};

/**
 * Arpeggio Module.
 * @module arpeggio
 */

/**
 * Alberti bass
 *
 * mode 1: lowest, highest, middle, highest
 *
 * mode 2: highest, lowest, middle, lowest
 *
 * mode 3: lowest, middle, highest, middle
 *
 * @function
 * @param {object} {chord,mode,octave} options object
 * @param {array} options.chord Array[String]
 * @param {number} options.mode Mode Number
 * @param {number} options.octave Octave Number
 * @param {object} ...options.rest
 * @returns {array} array of notes with octave appended
 */
const alberti = ({ chord, mode = 1, octave, ...rest }) => {
  if (chord.length > 3 || chord.length < 3) {
    throw new Error('Chord should be a triad');
  }
  let chordWithOctaveNumber = appendOctaveChord({ chord, octave, ...rest });
  if (containsOctaveNumber(chord[0])) {
    chordWithOctaveNumber = chord;
  }
  const highestIndex = chord.length - 1;
  const lowest = chordWithOctaveNumber[0];
  const middle = chordWithOctaveNumber[1];
  const highest = chordWithOctaveNumber[highestIndex];

  if (mode === 2) {
    return [highest, lowest, middle, lowest];
  }

  if (mode === 3) {
    return [lowest, middle, highest, middle];
  }

  return [lowest, highest, middle, highest];
};

const alberti1 = alberti;

const alberti2 = ({ chord, octave, ...rest }) =>
  alberti({
    chord,
    mode: 2,
    octave,
    ...rest,
  });

const alberti3 = ({ chord, octave, ...rest }) =>
  alberti({
    chord,
    mode: 3,
    octave,
    ...rest,
  });

/**
 * Ballad
 * root, fifth, third(higher octave), fifth
 * @function
 * @param {object} {chord,octave} options object
 * @param {array} options.chord Array[String] chord should be triad in first inversion
 * @param {number} options.octave Octave Number
 * @param {object} ...options.rest
 * @returns {array} array of notes with octave appended
 */
const ballad = ({ chord, octave, ...rest }) => {
  if (chord.length > 3 || chord.length < 3) {
    throw new Error('Chord should be a triad');
  }
  const firstNote = chord[0];
  const fifthNote = chord[2];

  // check if chord is in first inversion
  // major, triad, aug and diminished triad
  if (
    getInterval(firstNote, fifthNote) !== 7 &&
    getInterval(firstNote, fifthNote) !== 6 &&
    getInterval(firstNote, fifthNote) !== 8
  ) {
    throw new Error('Chord should be a Triad in first inversion');
  }

  let chordWithOctaveNumber = appendOctaveChord({ chord, octave, ...rest });
  if (containsOctaveNumber(chord[0])) {
    chordWithOctaveNumber = chord;
  }
  const fifthIndex = chord.length - 1;
  const lowest = chordWithOctaveNumber[0];
  const higherOctaveThird = incOctave(chordWithOctaveNumber[1]);
  const fifth = chordWithOctaveNumber[fifthIndex];

  return [lowest, fifth, higherOctaveThird, fifth, higherOctaveThird, fifth];
};

/**
 * Build Arpeggio on chords
 * Supports all types of chords
 * @function
 * @param {object} {chord,numberOfOctaves,octave,reverse,repeatOctave} options object
 * @param {array} options.chord chord array
 * @param {number} options.octave Number
 * @param {number} options.numberOfOctaves Number
 * @param {boolean} options.reverse Boolean
 * @param {boolean} options.includeFirsNote Boolean include first note when reversing
 * @param {boolean} options.repeatOctave Boolean
 * @returns {array} array of notes with octave appended
 */
const build = ({
  chord,
  numberOfOctaves = 1,
  octave = 4,
  reverse = false,
  includeFirsNote = true,
  repeatOctave = false,
  ...rest
}) => {
  let chordWithOctaveNumber = appendOctaveChord({ chord, octave, ...rest });
  // let chordWithOctaveNumber = chord;
  let currentOctave = octave;
  // if the chord has 2 notes
  if (chord.length === 2) {
    let result = [];
    for (let i = 0; i < numberOfOctaves; i += 1) {
      result = result.concat(chordWithOctaveNumber);
      currentOctave += 1;
      chordWithOctaveNumber = appendOctaveChord({
        chord,
        octave: currentOctave,
        ...rest,
      });
    }
    // add first note as higher octave note
    const octaveHigher = chordWithOctaveNumber[0];
    result.push(octaveHigher);
    if (reverse) {
      return result.concat(
        arrayRev({ array: result, from: repeatOctave ? 0 : 1 }),
      );
    }
    return result;
  }

  // if the chord is a triad, add one octave higher note of lowest note.
  if (chord.length === 3) {
    let result = [];
    for (let i = 0; i < numberOfOctaves; i += 1) {
      result = result.concat(chordWithOctaveNumber);
      currentOctave += 1;
      chordWithOctaveNumber = appendOctaveChord({
        chord,
        octave: currentOctave,
        ...rest,
      });
    }
    // add higher octave base note to chord at last
    const octaveHigher = chordWithOctaveNumber[0];
    result.push(octaveHigher);
    if (reverse) {
      return result.concat(
        arrayRev({
          array: result,
          from: repeatOctave ? 0 : 1,
          to: includeFirsNote ? result.length : result.length - 1,
        }),
      );
    }
    return result;
  }
  // for other chords
  // if the chord is 6th/7th/9th/11th/13th
  let result = [];
  for (let i = 0; i < numberOfOctaves; i += 1) {
    result = result.concat(chordWithOctaveNumber);
    currentOctave += 1;
    chordWithOctaveNumber = appendOctaveChord({ chord, octave: currentOctave });
  }
  if (reverse) {
    // don't repeat octave for 6th/7th chords and extended chords
    return result.concat(
      arrayRev({
        array: result,
        from: 1,
        to: includeFirsNote ? result.length : result.length - 1,
      }),
    );
  }
  return result;
};

/**
 * Arpegiate chord in 1, 3, 5, 8, 5, 3 fashion
 * For 7th chord it is 1, 3, 5, 7, 5, 3
 * @function
 * @param {object} {chord,octave} options object
 * @param {array} options.chord chord array
 * @param {number} options.octave Number
 * @returns {array} array of notes with octave appended
 */
const arpeggiate1 = ({ chord, octave, ...rest }) => {
  if (chord.length < 3) {
    throw new Error('Chord should be a triad or 7th');
  }
  const includeFirsNote = false;
  const reverse = true;
  return build({
    chord,
    octave,
    reverse,
    includeFirsNote,
    ...rest,
  });
};

/**
 * Returns chromatic note index from diatonic index
 * Alternative of one-to-one map from scales.majorScaleIntervalToChromaticIndexMap
 * TODO
 */
// export const diatonicToChromaticStep = (diatonicStep) => {
//   const intervalNumber = getNumberFromIntervalString(intervalString);
//   let chromaticIntervalFromRoot = majorScaleIntervalToChromaticIndexMap[intervalNumber];
//
//   if (isFlatInterval(intervalString)) {
//     chromaticIntervalFromRoot -= 1;
//   }
//
//   if (isSharpInterval(intervalString)) {
//     chromaticIntervalFromRoot += 1;
//   }
// }

const isMinorChord = (chord) => /[A-H#b]m/.test(chord);

/**
 * Build Arpeggio from formula
 * WIP
 * @function
 * @param {object} {note,formula} options object
 * @param {string} options.note note name or even chords
 * @param {array} options.formula Number
 * @param {number} options.octave Number
 * @returns {array} array of notes with octave appended
 */
const buildFromFormula = ({
  note,
  formula,
  octave = 4,
  objectResult,
}) => {
  const noteName = getNoteWithoutOctave(note);
  const startNoteIndex = getNoteIndex(noteName);
  if (startNoteIndex === -1) {
    throw new Error(`Note Not Found: ${note}`);
  }

  const arpeggioNotes = [];
  const buildNote = (intervalString) => {
    // TODO support for inversion, handle base note
    // if chord has base note, arpeggio should start from that note
    const intervalNumber = getNumberFromIntervalString(intervalString);
    let chromaticIntervalFromRoot = majorScaleIntervalToChromaticIndexMap[intervalNumber];

    // if minor chord
    if (isMinorChord(note)) {
      chromaticIntervalFromRoot = minorScaleIntervalToChromaticIndexMap[intervalNumber];
    }

    if (isFlatInterval(intervalString)) {
      chromaticIntervalFromRoot -= 1;
    }

    if (isSharpInterval(intervalString)) {
      chromaticIntervalFromRoot += 1;
    }

    const noteIndex = (startNoteIndex + chromaticIntervalFromRoot) % 12;

    const currentNote = getNote({
      chromaticNote: CHROMATIC_NOTES[noteIndex],
      noteToBuild: note,
      isFlatNote: isFlatNote(note) || isFlatInterval(intervalString) || isMinorChord(note),
    });

    const octaveMultiplier = parseInt(intervalNumber / 8, 0);
    let currentOctave = octave + octaveMultiplier;

    const currentNoteIndex = getNoteIndex(CHROMATIC_NOTES[noteIndex]);

    if (currentNoteIndex < startNoteIndex) {
      currentOctave += 1;
    }
    if (objectResult) {
      return {
        note: currentNote,
        octave: currentOctave,
      };
    }
    return currentNote + currentOctave;
  };

  formula.forEach((intervalString, index) => {
    // check for polyphonic arpeggio
    if (Array.isArray(intervalString)) {
      const currentNotes = intervalString.map(buildNote);
      arpeggioNotes.push(currentNotes);
      return;
    }
    arpeggioNotes.push(buildNote(intervalString));
  });

  return arpeggioNotes;
};

exports.alberti = alberti;
exports.alberti1 = alberti1;
exports.alberti2 = alberti2;
exports.alberti3 = alberti3;
exports.arpeggiate1 = arpeggiate1;
exports.ballad = ballad;
exports.build = build;
exports.buildFromFormula = buildFromFormula;
exports.isMinorChord = isMinorChord;
